import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Target, Users, BarChart3, MessageSquare, CheckCircle, Download, Clock, Star, TrendingUp, BookOpen, Settings, FileText } from 'lucide-react';
import { useFocusAreasData, useQuestionTypesData } from '@/hooks/useSurveyData';
import { useSurvey } from '@/contexts/SurveyContext';
import { SurveyTemplateCatalogDialog } from '@/components/survey/SurveyTemplateCatalogDialog';
import { EmailRequestDialog } from '@/components/survey/EmailRequestDialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { FocusAreaCard } from '@/components/survey/FocusAreaCard';
import { QuestionTypeCard } from '@/components/survey/QuestionTypeCard';
import { getQuestionTypeIcon } from '@/utils/questionTypeIcons';
import { PaginationControls } from '@/components/homepage/PaginationControls';
import { PaginationInfo } from '@/components/homepage/PaginationInfo';

interface SurveyConfig {
  companySize: string;
  industry: string;
  focusAreas: string[];
  questionCount: number;
  questionTypes: string[];
  surveyFrequency: string;
  targetAudience: string;
  additionalContext: string;
}

interface GeneratedQuestion {
  id: string;
  type: string;
  category: string;
  question: string;
  options?: string[];
  scale?: { min: number; max: number; labels?: string[] };
}

const SurveyGenerator: React.FC = () => {
  const navigate = useNavigate();
  const { toolId, lang } = useParams<{ toolId: string; lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const { config, setConfig, setGeneratedQuestions, isGenerating, setIsGenerating } = useSurvey();
  const { toast } = useToast();
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: focusAreas, isLoading: focusAreasLoading } = useFocusAreasData();
  const { data: questionTypes, isLoading: questionTypesLoading } = useQuestionTypesData();

  const fallbackFocusAreas = [
    'Pre-boarding Experience',
    'First Day Experience', 
    'Training & Development',
    'Manager Support',
    'Team Integration',
    'Role Clarity',
    'Resources & Tools',
    'Company Culture Alignment',
    'Social Connections',
    'Feedback & Communication',
    'Goal Setting',
    'Expectations vs Reality'
  ];

  const fallbackQuestionTypes = [
    'Likert Scale (1-5)',
    'Rating Scale (1-10)',
    'Yes/No',
    'Multiple Choice',
    'Open-ended',
    'Ranking'
  ];

  // Focus Areas pagination
  const [focusPage, setFocusPage] = useState(1);
  const focusPageSize = 10;

  useEffect(() => {
    // Reset page when data source changes
    setFocusPage(1);
  }, [focusAreasLoading]);

  const allFocusAreas = (focusAreas && focusAreas.length > 0
    ? focusAreas
    : fallbackFocusAreas.map((name, index) => ({
        id: `fallback-${index}`,
        name,
        description: '',
        icon: 'star',
        isPopular: false
      })));

  const totalFocusAreas = allFocusAreas.length;
  const totalFocusPages = Math.max(1, Math.ceil(totalFocusAreas / focusPageSize));
  const pagedFocusAreas = allFocusAreas.slice(
    (focusPage - 1) * focusPageSize,
    focusPage * focusPageSize
  );

  const handleFocusAreaChange = (area: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      focusAreas: checked 
        ? [...prev.focusAreas, area]
        : prev.focusAreas.filter(a => a !== area)
    }));
  };

  const handleQuestionTypeChange = (type: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      questionTypes: checked 
        ? [...prev.questionTypes, type]
        : prev.questionTypes.filter(t => t !== type)
    }));
  };

  const generateQuestions = () => {
    // Defer actual generation to the results page to ensure LoadingState is shown immediately
    setIsGenerating(true);
    setGeneratedQuestions([]);
    navigate(`${prefix}/tools/${toolId}/results`);
  };

  const handleEmailContinue = () => {
    const email = emailInput.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      toast({ title: t('survey.emailDialog.invalidTitle'), description: t('survey.emailDialog.invalid'), variant: 'destructive' });
      return;
    }
    setConfig(prev => ({ ...prev, email }));
    setShowEmailDialog(false);
    generateQuestions();
  };

  const isConfigValid = () => {
    return config.companySize && config.industry && config.surveyFrequency && config.targetAudience;
  };

  const getConfigProgress = () => {
    const required = ['companySize', 'industry', 'surveyFrequency', 'targetAudience'] as const;
    const completed = required.filter(field => (config as any)[field]).length;
    return (completed / required.length) * 100;
  };
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Configuration Form */}
      <Card className="shadow-card">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              {t('survey.config.title')}
            </CardTitle>
            <Button 
              variant="outline" 
              onClick={() => setShowTemplateDialog(true)}
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              <BookOpen className="h-4 w-4" />
              {t('survey.config.browseTemplates')}
            </Button>
          </div>
          {/* Progress Indicator */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('survey.config.progress')}</span>
              <span className="font-medium">{Math.round(getConfigProgress())}%</span>
            </div>
            <Progress value={getConfigProgress()} className="h-2" />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-size">{t('survey.config.companySize')} <span className="text-destructive">*</span></Label>
                <Select value={config.companySize} onValueChange={(value) => setConfig(prev => ({ ...prev, companySize: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={<span className="text-muted-foreground italic">{t('survey.config.placeholders.companySize')}</span>} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="startup">{t('survey.config.sizeOptions.startup')}</SelectItem>
                    <SelectItem value="small">{t('survey.config.sizeOptions.small')}</SelectItem>
                    <SelectItem value="medium">{t('survey.config.sizeOptions.medium')}</SelectItem>
                    <SelectItem value="large">{t('survey.config.sizeOptions.large')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">{t('survey.config.industry')} <span className="text-destructive">*</span></Label>
                <Select value={config.industry} onValueChange={(value) => setConfig(prev => ({ ...prev, industry: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={<span className="text-muted-foreground italic">{t('survey.config.placeholders.industry')}</span>} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">{t('survey.config.industryOptions.technology')}</SelectItem>
                    <SelectItem value="healthcare">{t('survey.config.industryOptions.healthcare')}</SelectItem>
                    <SelectItem value="finance">{t('survey.config.industryOptions.finance')}</SelectItem>
                    <SelectItem value="retail">{t('survey.config.industryOptions.retail')}</SelectItem>
                    <SelectItem value="manufacturing">{t('survey.config.industryOptions.manufacturing')}</SelectItem>
                    <SelectItem value="education">{t('survey.config.industryOptions.education')}</SelectItem>
                    <SelectItem value="consulting">{t('survey.config.industryOptions.consulting')}</SelectItem>
                    <SelectItem value="other">{t('survey.config.industryOptions.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('survey.config.surveyFrequency')} <span className="text-destructive">*</span></Label>
                <Select value={config.surveyFrequency} onValueChange={(value) => setConfig(prev => ({ ...prev, surveyFrequency: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={<span className="text-muted-foreground italic">{t('survey.config.placeholders.surveyFrequency')}</span>} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{t('survey.config.frequencyOptions.weekly')}</SelectItem>
                    <SelectItem value="monthly">{t('survey.config.frequencyOptions.monthly')}</SelectItem>
                    <SelectItem value="quarterly">{t('survey.config.frequencyOptions.quarterly')}</SelectItem>
                    <SelectItem value="annual">{t('survey.config.frequencyOptions.annual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('survey.config.questionCount')}</Label>
                <Input
                  type="number"
                  min="5"
                  max="25"
                  value={config.questionCount}
                  onChange={(e) => setConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) || 10 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('survey.config.targetAudience')} <span className="text-destructive">*</span></Label>
              <Select value={config.targetAudience} onValueChange={(value) => setConfig(prev => ({ ...prev, targetAudience: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder={<span className="text-muted-foreground italic">{t('survey.config.placeholders.targetAudience')}</span>} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('survey.config.audienceOptions.all')}</SelectItem>
                  <SelectItem value="management">{t('survey.config.audienceOptions.management')}</SelectItem>
                  <SelectItem value="individual-contributors">{t('survey.config.audienceOptions.individual-contributors')}</SelectItem>
                  <SelectItem value="specific-department">{t('survey.config.audienceOptions.specific-department')}</SelectItem>
                  <SelectItem value="new-hires">{t('survey.config.audienceOptions.new-hires')}</SelectItem>
                  <SelectItem value="remote-workers">{t('survey.config.audienceOptions.remote-workers')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Focus Areas */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">{t('survey.config.focusAreas')}</Label>
            {config.focusAreas.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {config.focusAreas.map(area => (
                  <Badge key={area} variant="secondary" className="text-xs">
                    {area}
                  </Badge>
                ))}
              </div>
            )}
            {focusAreasLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : (
              <>
                <PaginationInfo
                  currentPage={focusPage}
                  pageSize={focusPageSize}
                  totalItems={totalFocusAreas}
                  itemLabel="focus areas"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pagedFocusAreas.map((area) => (
                    <FocusAreaCard
                      key={area.id}
                      id={area.id}
                      name={area.name}
                      description={area.description}
                      icon={area.icon}
                      isPopular={area.isPopular}
                      isSelected={config.focusAreas.includes(area.name)}
                      onSelect={(selected) => handleFocusAreaChange(area.name, selected as boolean)}
                    />
                  ))}
                </div>
                <PaginationControls
                  currentPage={focusPage}
                  totalPages={totalFocusPages}
                  onPageChange={setFocusPage}
                />
              </>
            )}
            
          </div>

          <Separator />

          {/* Question Types */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">{t('survey.config.questionTypes')}</Label>
            {questionTypesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(questionTypes && questionTypes.length > 0 ? questionTypes : fallbackQuestionTypes.map((name, index) => ({
                  id: `fallback-${index}`,
                  name,
                  description: '',
                  icon: null
                }))).map((type) => (
                  <QuestionTypeCard
                    key={type.id}
                    type={type.name}
                    description={type.description}
                    icon={getQuestionTypeIcon(type.icon || type.name)}
                    isSelected={config.questionTypes.includes(type.name)}
                    onSelect={(selected) => handleQuestionTypeChange(type.name, (selected as boolean))}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="context">{t('survey.config.additionalContext')}</Label>
            <Textarea
              id="context"
              placeholder={t('survey.config.additionalContextPlaceholder')}
              value={config.additionalContext}
              onChange={(e) => setConfig(prev => ({ ...prev, additionalContext: e.target.value }))}
              rows={3}
            />
          </div>

          <Button 
            onClick={() => {
              if (user) {
                generateQuestions();
              } else {
                setEmailInput(config.email || "");
                setShowEmailDialog(true);
              }
            }}
            disabled={!isConfigValid() || isGenerating}
            className="w-full"
            variant="gradient"
            size="lg"
          >
            {isGenerating ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('survey.config.generating')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                {t('survey.config.generate')}
              </div>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Template Catalog Dialog */}
      <SurveyTemplateCatalogDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        onUseTemplate={(templateId) => {
          // Navigate to results page with the selected template
          navigate(`${prefix}/tools/${toolId}/results?surveyTemplateId=${templateId}`);
        }}
      />

      {/* Email Request Dialog */}
      <EmailRequestDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        emailInput={emailInput}
        onEmailInputChange={setEmailInput}
        onSubmit={handleEmailContinue}
        toolId={toolId || ''}
        lang={lang}
      />
    </div>
  );
};

export default SurveyGenerator;